import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  WirCreateTab,
  WirCreateTabs,
  WirInterventionTab,
  WirInterventionTabs,
  WirIntervSelectTabs,
} from 'pages/WellGuardian/WIR/wir.constants';
import {
  Alert,
  AlertsCount,
  AllWellsViewMode,
  AssetsUk,
  AssetType,
  ChecklistQuestions,
  Order,
  R2SLink,
  SitesUk,
  SiteType,
  WellInfo,
  WellInfoForSite,
  WIR,
  WIRComment,
  WIRListPresentation,
  WIRUser,
  WSType,
} from 'types/wellGuardian';

export type WellGuardianState = Readonly<{
  wellInfo: WellInfo | null;
  alertsCount: AlertsCount | null;
  r2SLink: R2SLink | null;
  selectedAsset: AssetType;
  selectedSite: SiteType;
  sortCheck: boolean;
  wellsInfo: WellInfo[];
  wellSortOrder: Order;
  wellSort: string;
  wellFilter: string;
  interventionFilter: string;
  engineerFilter: string;
  caseFilter: string;
  phaseFilter: string;
  priorityFilter: string;
  scopeFilter: string;
  synergiCount: number;
  derogCount: number;
  wellManagementModal: string;
  wellData: WellInfo | null;
  allWellsSearchInput: string;
  fetchReload: boolean;
  isSiteLayoutAvailable: boolean;
  allWellsViewMode: AllWellsViewMode;
  siteActiveAlerts: Alert[];
  reTrigger: boolean;
  selectedWIR: WIR | null;
  selectTab: string;
  errorCheck: boolean;
  selectCommentWIR: WIRComment | null;
  originalSelectedWIR: WIR | null;
  selectedSection: string;
  activeWIRStep: number;
  wirUsers: WIRUser[];
  wirList: WIRListPresentation[];
  wirCreateSelectedTab: WirCreateTab;
  selectedInfo: string;
  infoTips: boolean | null;
  downloadPDF: boolean;
  wirDraftComment: string;
  interventionDraftComment: string;
  wirInterventionSelectedTab: WirInterventionTab;
  countryState: string;
  wellSiteList: WellInfoForSite[];
  reviewChecklist: ChecklistQuestions | null;
  selectedWSActiveJob: string;
  selectedWSTask: WSType | null;
  primaveraJobId: string | undefined;
  deadlineCheck: boolean;
  allWirUsers: WIRUser[];
}>;

export const initialState: WellGuardianState = {
  wellInfo: null,
  alertsCount: null,
  r2SLink: null,
  selectedAsset: AssetsUk.ALL,
  selectedSite: SitesUk.ALL,
  sortCheck: false,
  wellsInfo: [],
  wellSortOrder: {
    wellName: 'asc',
    slot: 'asc',
    type: 'asc',
    status: 'asc',
    phase: 'asc',
  },
  wellSort: 'WellName',
  wellFilter: 'N/A',
  interventionFilter: 'N/A',
  engineerFilter: 'N/A',
  caseFilter: 'N/A',
  phaseFilter: 'N/A',
  synergiCount: 0,
  derogCount: 0,
  priorityFilter: 'N/A',
  scopeFilter: 'N/A',
  wellManagementModal: '',
  wellData: null,
  allWellsSearchInput: '',
  fetchReload: false,
  isSiteLayoutAvailable: true,
  allWellsViewMode: 'grid',
  siteActiveAlerts: [],
  reTrigger: false,
  selectedWIR: null,
  selectTab: WirIntervSelectTabs.REQUEST_DETAILS_LABEL,
  errorCheck: false,
  selectCommentWIR: null,
  originalSelectedWIR: null,
  activeWIRStep: 0,
  selectedSection: '',
  wirUsers: [],
  wirList: [],
  wirCreateSelectedTab: WirCreateTabs.ACTIVITY_LABEL,
  selectedInfo: 'ALL',
  infoTips: null,
  downloadPDF: false,
  wirDraftComment: '',
  interventionDraftComment: '',
  wirInterventionSelectedTab: WirInterventionTabs.LIST,
  countryState: '',
  wellSiteList: [],
  reviewChecklist: null,
  selectedWSActiveJob: 'N/A',
  selectedWSTask: null,
  primaveraJobId: '',
  deadlineCheck: false,
  allWirUsers: [],
};

const wellGuardianSlice = createSlice({
  name: 'User',
  initialState,
  reducers: {
    updateWellInfo: (state, action: PayloadAction<WellInfo | null>) => {
      state.wellInfo = action.payload;
    },
    updateAlertsCount: (state, action: PayloadAction<AlertsCount | null>) => {
      state.alertsCount = action.payload;
    },
    updateR2SLink: (state, action: PayloadAction<R2SLink | null>) => {
      state.r2SLink = action.payload;
    },
    updateSelectedAsset: (state, action: PayloadAction<AssetType>) => {
      state.selectedAsset = action.payload;
    },
    updateSelectedSite: (state, action: PayloadAction<SiteType>) => {
      state.selectedSite = action.payload;
    },
    updateSortCheck: (state, action: PayloadAction<boolean>) => {
      state.sortCheck = action.payload;
    },
    updateSortedWells: (state, action: PayloadAction<WellInfo[]>) => {
      state.wellsInfo = action.payload;
    },
    updateWellSortOrder: (state, action: PayloadAction<Order>) => {
      state.wellSortOrder = action.payload;
    },
    updateWellSort: (state, action: PayloadAction<string>) => {
      state.wellSort = action.payload;
    },
    updateWellFilter: (state, action: PayloadAction<string>) => {
      state.wellFilter = action.payload;
    },
    updateInterventionFilter: (state, action: PayloadAction<string>) => {
      state.interventionFilter = action.payload;
    },
    updateEngineerFilter: (state, action: PayloadAction<string>) => {
      state.engineerFilter = action.payload;
    },
    updateCaseFilter: (state, action: PayloadAction<string>) => {
      state.caseFilter = action.payload;
    },
    updatePhaseFilter: (state, action: PayloadAction<string>) => {
      state.phaseFilter = action.payload;
    },
    updatePriorityFilter: (state, action: PayloadAction<string>) => {
      state.priorityFilter = action.payload;
    },
    updateScopeFilter: (state, action: PayloadAction<string>) => {
      state.scopeFilter = action.payload;
    },
    updateCountSynergi: (state, action: PayloadAction<number>) => {
      state.synergiCount = action.payload;
    },
    updateCountDerog: (state, action: PayloadAction<number>) => {
      state.derogCount = action.payload;
    },
    updateIsSiteLayoutAvailable: (state, action: PayloadAction<boolean>) => {
      state.isSiteLayoutAvailable = action.payload;
    },
    updateSiteActiveAlerts: (state, action: PayloadAction<Alert[]>) => {
      state.siteActiveAlerts = action.payload;
    },
    updateWellManagementModal: (state, action: PayloadAction<string>) => {
      state.wellManagementModal = action.payload;
    },
    updateWellData: (state, action: PayloadAction<WellInfo>) => {
      state.wellData = action.payload;
    },
    updateAllWellsSearchInput: (state, action: PayloadAction<string>) => {
      state.allWellsSearchInput = action.payload;
    },
    updateReloadFetch: (state, action: PayloadAction<boolean>) => {
      state.fetchReload = action.payload;
    },
    updateAllWellsViewMode: (state, action: PayloadAction<AllWellsViewMode>) => {
      state.allWellsViewMode = action.payload;
    },
    reTriggerPrData: (state, action: PayloadAction<boolean>) => {
      state.reTrigger = action.payload;
    },
    updateSelectedWIR: (state, action: PayloadAction<WIR | null>) => {
      state.selectedWIR = action.payload;
    },
    updateInterventionTabs: (state, action: PayloadAction<string>) => {
      state.selectTab = action.payload;
    },
    updateErrorCheck: (state, action: PayloadAction<boolean>) => {
      state.errorCheck = action.payload;
    },
    updateSelectCommentWIR: (state, action: PayloadAction<WIRComment | null>) => {
      state.selectCommentWIR = action.payload;
    },
    updateOriginalSelectedWIR: (state, action: PayloadAction<WIR | null>) => {
      state.originalSelectedWIR = action.payload;
    },
    updateSelectedSection: (state, action: PayloadAction<string>) => {
      state.selectedSection = action.payload;
    },
    updateActiveWIRStep: (state, action: PayloadAction<number>) => {
      state.activeWIRStep = action.payload;
    },
    updateWIRUsers: (state, action: PayloadAction<WIRUser[]>) => {
      state.wirUsers = action.payload;
    },
    updateWIRList: (state, action: PayloadAction<WIRListPresentation[]>) => {
      state.wirList = action.payload;
    },
    updateWIRCreateSelectedTab: (state, action: PayloadAction<WirCreateTab>) => {
      state.wirCreateSelectedTab = action.payload;
    },
    updateInfoTab: (state, action: PayloadAction<string>) => {
      state.selectedInfo = action.payload;
    },
    updateInfoTips: (state, action: PayloadAction<boolean | null>) => {
      state.infoTips = action.payload;
    },
    updateDownload: (state, action: PayloadAction<boolean>) => {
      state.downloadPDF = action.payload;
    },
    updateWIRDraftComment: (state, action: PayloadAction<string>) => {
      state.wirDraftComment = action.payload;
    },
    updateInterventionDraftComment: (state, action: PayloadAction<string>) => {
      state.interventionDraftComment = action.payload;
    },
    updateWIRInterventionSelectedTab: (state, action: PayloadAction<WirInterventionTab>) => {
      state.wirInterventionSelectedTab = action.payload;
    },
    updateCountryState: (state, action: PayloadAction<string>) => {
      state.countryState = action.payload;
    },
    updateReviewCheckList: (state, action: PayloadAction<ChecklistQuestions | null>) => {
      state.reviewChecklist = action.payload;
    },
    updateSelectedWSActiveJob: (state, action: PayloadAction<string>) => {
      state.selectedWSActiveJob = action.payload;
    },
    updateSelectedWSTask: (state, action: PayloadAction<WSType | null>) => {
      state.selectedWSTask = action.payload;
    },
    updateDeadlineCheck: (state, action: PayloadAction<boolean>) => {
      state.deadlineCheck = action.payload;
    },
    updatePrimaveraJobId: (state, action: PayloadAction<string | undefined>) => {
      state.primaveraJobId = action.payload;
    },
    updateAllWIRUsers: (state, action: PayloadAction<WIRUser[]>) => {
      state.allWirUsers = action.payload;
    },
  },
});

export const {
  updateWellInfo,
  updateAlertsCount,
  updateR2SLink,
  updateSelectedAsset,
  updateSelectedSite,
  updateSortCheck,
  updateSortedWells,
  updateWellSortOrder,
  updateWellSort,
  updateWellFilter,
  updateInterventionFilter,
  updateEngineerFilter,
  updateCaseFilter,
  updatePhaseFilter,
  updatePriorityFilter,
  updateScopeFilter,
  updateCountSynergi,
  updateCountDerog,
  updateIsSiteLayoutAvailable,
  updateSiteActiveAlerts,
  updateWellManagementModal,
  updateWellData,
  updateAllWellsSearchInput,
  updateReloadFetch,
  updateAllWellsViewMode,
  reTriggerPrData,
  updateSelectedWIR,
  updateInterventionTabs,
  updateErrorCheck,
  updateOriginalSelectedWIR,
  updateSelectedSection,
  updateActiveWIRStep,
  updateWIRUsers,
  updateWIRList,
  updateWIRCreateSelectedTab,
  updateInfoTab,
  updateInfoTips,
  updateDownload,
  updateWIRDraftComment,
  updateInterventionDraftComment,
  updateWIRInterventionSelectedTab,
  updateCountryState,
  updateReviewCheckList,
  updateSelectedWSActiveJob,
  updateSelectedWSTask,
  updateDeadlineCheck,
  updatePrimaveraJobId,
  updateAllWIRUsers,
} = wellGuardianSlice.actions;
export default wellGuardianSlice.reducer;
